import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Section, SectionHeading, Reveal, Placeholder } from "@/components/primitives";
import { getContent, api, formatApiErrorDetail, trackEvent } from "@/lib/api";
import { toast } from "sonner";
import { Handshake, GraduationCap, UsersThree, Megaphone } from "@phosphor-icons/react";

const PILLARS = [
  { icon: Megaphone, title: "Technology Adoption", desc: "Introduce users to sophisticated arbitrage technology." },
  { icon: GraduationCap, title: "Customer Education", desc: "Help members understand the technology, architecture, and risks." },
  { icon: UsersThree, title: "Community Building", desc: "Build informed, engaged communities around the platform." },
  { icon: Handshake, title: "Leadership & Training", desc: "Develop leadership through training and product awareness." },
];

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm text-[#cbd5e1] mb-1.5">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#08090F] px-4 py-3 text-white placeholder:text-[#475569] focus:border-[#2DD4BF]/50 focus:outline-none focus:ring-1 focus:ring-[#2DD4BF]/30 transition-colors";

export default function Partners() {
  const [tiers, setTiers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", country: "", phone: "", experience: "", program: "Partner", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    trackEvent("page_view", { page: "partners" });
    getContent("partner_tiers").then(setTiers).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/partners/apply", form);
      trackEvent("partner_application");
      toast.success("Application received. Our team will be in touch.");
      setForm({ name: "", email: "", country: "", phone: "", experience: "", program: "Partner", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Partner Program"
        title="Partner With Quantum Scalp."
        sub="Qualified partners can introduce users to the platform and participate in our approved referral/affiliate program — emphasizing technology adoption, education, and community, not recruitment."
      />

      <Section grid>
        <SectionHeading eyebrow="What We Emphasize" title="Build understanding, not hype." className="mb-12" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.05}>
              <div className="h-full rounded-xl border border-white/10 bg-[#0A0B10] p-6">
                <span className="grid place-items-center h-11 w-11 rounded-lg border border-white/10 bg-white/[0.03] text-[#2DD4BF] mb-4">
                  <p.icon size={22} weight="duotone" />
                </span>
                <h3 className="text-base font-bold text-white mb-1.5">{p.title}</h3>
                <p className="text-sm text-[#94A3B8]">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section grid>
        <SectionHeading
          eyebrow="Compensation Structure"
          title="A flexible, configurable plan."
          sub="Values are configurable and subject to the official, legally approved compensation plan."
          className="mb-10"
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="partner-tiers">
          {tiers.map((t, i) => (
            <Reveal key={t.id} delay={(i % 3) * 0.05}>
              <div className="h-full rounded-xl border border-white/10 bg-[#0A0B10] p-6">
                <h3 className="text-base font-bold text-white mb-2">{t.name}</h3>
                <p className="text-sm text-[#94A3B8] mb-4">{t.desc}</p>
                <Placeholder>{t.value}</Placeholder>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-8 font-mono text-xs text-[#64748B]">
          All compensation is subject to the official compensation plan, applicable terms, and geographic restrictions.
        </p>
      </Section>

      <Section grid>
        <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <SectionHeading eyebrow="Apply" title="Become a partner." sub="Tell us about yourself. Applications are reviewed and subject to approval." />
          </div>
          <form onSubmit={submit} className="lg:col-span-3 rounded-2xl border border-white/10 bg-[#0A0B10] p-6 md:p-8 space-y-4" data-testid="partner-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full Name *">
                <input data-testid="partner-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" />
              </Field>
              <Field label="Email *">
                <input data-testid="partner-email" type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@example.com" />
              </Field>
              <Field label="Country">
                <input data-testid="partner-country" className={inputCls} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Country" />
              </Field>
              <Field label="Phone">
                <input data-testid="partner-phone" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1..." />
              </Field>
            </div>
            <Field label="Program">
              <select data-testid="partner-program" className={inputCls} value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })}>
                <option className="bg-[#08090F]">Partner</option>
                <option className="bg-[#08090F]">Master Distributor</option>
                <option className="bg-[#08090F]">Leadership</option>
              </select>
            </Field>
            <Field label="Relevant Experience">
              <input data-testid="partner-experience" className={inputCls} value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} placeholder="Community building, sales, tech..." />
            </Field>
            <Field label="Message">
              <textarea data-testid="partner-message" rows={4} className={inputCls} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us more..." />
            </Field>
            <button
              type="submit"
              disabled={submitting}
              data-testid="partner-submit"
              className="w-full px-6 py-3.5 rounded-full bg-[#2DD4BF] text-[#030305] font-semibold hover:bg-white transition-colors disabled:opacity-60"
            >
              {submitting ? "Submitting…" : "Submit Application"}
            </button>
          </form>
        </div>
      </Section>
    </div>
  );
}
